import { useDispatch, useSelector } from "react-redux";
import { Label } from "../main/component/ProjectForm";
import DataPicker from "./DataPicker";
import GoalAction from "../actions/GoalAction";
import { fromJS } from "immutable";
import InputInline from "../components/InputInline";
import LinkItemPickerV2 from "../main/component/LinkItemPickerV2";
import DatePicker from "../board/components/TaskDetail/DatePicker";
import { isBefore, isValid } from "date-fns";
import React, { useEffect, useState } from "react";
import ConfirmDialog from "../dialog/ConfirmDialog";
import { useTranslation } from "react-i18next";

export default function Goal({ projectId, goalId, onClose = () => {} }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [errors, setErrors] = useState({});
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const pipelines = useSelector((state: any) => state.bucketReducer.get("SALE"));
  const members = useSelector((state: any) => state.projectReducer.getIn(["project", "members"]));
  const goal = useSelector((state: any) => state.goal.get("goal"));
  const goalTypes = useSelector((state: any) => state.goal.get("goalTypes"));
  const scopeTypes = useSelector((state: any) => state.goal.get("scopeTypes"));
  const frequencies = useSelector((state: any) => state.goal.get("frequencies"));
  const trackingMetrics = useSelector((state: any) => state.goal.get("trackingMetrics"));

  const selectedGoalType =
    goalTypes.find((item) => item.get("value") == goal.get("type")) || fromJS({});

  const selectedScopeType =
    scopeTypes.find((item) => item.get("value") == goal.getIn(["scope", "type"])) || fromJS({});

  const buckets = goal.get("buckets").toJS();

  const pipelinesOptions = pipelines
    .map((pipe) => ({ value: pipe.get("id"), label: pipe.get("name") }))
    .toJS();

  const memberOptions = members.map((pipe) =>
    fromJS({ value: pipe.get("accountId"), label: pipe.get("displayName") })
  );

  const selectedScopeUserValue =
    memberOptions.find((item) => item.get("value") == goal.getIn(["scope", "value", "value"])) ||
    fromJS({});

  const selectedFrequency =
    frequencies.find((item) => item.get("value") == goal.getIn(["frequency"])) || fromJS({});

  const selectedTrackingMetric =
    trackingMetrics.find((item) => item.get("value") == goal.getIn(["trackingMetric"])) ||
    fromJS({});

  const handleOnChange = ({ fieldId, fieldValue }) => {
    const goalData = goal.setIn(fieldId, fieldValue);
    dispatch(GoalAction.setCurrentGoal(goalData.toJS()));
  };

  const handleOnSave = () => {
    const data = validateGoal();
    if (Object.keys(data).length == 0) {
      const request = {
        ...goal.toJS(),
        projectId,
        tenantId: "",
      };
      dispatch(GoalAction.saveGoal(request));
      onClose();
    }
    setErrors(data);
  };

  const isNewGoal = () => {
    return goalId == "new";
  };

  useEffect(() => {
    if (!isNewGoal()) {
      dispatch(GoalAction.getGoal(goalId));
    } else {
      dispatch(GoalAction.resetGoalForm());
    }
  }, [goalId]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderAssigneePicker = () => {
    if (goal.getIn(["scope", "type"]) == "User") {
      return (
        <DataPicker
          onChange={(value) => {
            handleOnChange({
              fieldId: ["scope", "value"],
              fieldValue: value,
            });
          }}
          items={memberOptions.toJS()}
          values={selectedScopeUserValue.toJS()}
          isMulti={false}
        />
      );
    } else if (goal.getIn(["scope", "type"]) == "Account") {
      return (
        <LinkItemPickerV2
          items={goal.getIn(["scope", "value"])}
          onChange={(value) => {
            handleOnChange({ fieldId: ["scope", "value"], fieldValue: value });
          }}
          type={"COMPANY"}
          projectId={projectId}
        />
      );
    }
    return null;
  };

  const validateAssignee = () => {
    const value = goal.getIn(["scope", "value"]) || "";
    const type = goal.getIn(["scope", "type"]) || "";
    if (value == "") {
      if (type == "Account") {
        return { assignee: t("Account is required") };
      }
      if (type == "User") {
        return { assignee: t("User is required") };
      }
    }
    return {};
  };

  const validatePipeline = () => {
    if (buckets.length == 0) {
      return { buckets: t("Pipelines are required") };
    }
    return {};
  };

  const validateDuration = () => {
    const from = goal.get("trackingFrom") || "";
    if (from == "") {
      return {
        trackingFrom: t("From is required"),
      };
    }
    const to = goal.get("trackingTo") || "";
    if (to != "") {
      const toDate = new Date(to);
      const fromDate = new Date(from);
      if (!isValid(toDate)) {
        return {
          trackingTo: t("Invalid date"),
        };
      } else if (!isBefore(fromDate, toDate)) {
        return {
          trackingTo: t("ToDate must be greater than FromDate"),
        };
      }
    }
    return {};
  };

  const validateTrackingMetric = () => {
    const trackingGoalValue = goal.getIn(["trackingGoal", "value"]) || "";
    const value = parseFloat(trackingGoalValue) || 0.0;
    if (value <= 0) {
      return { trackingMetric: t("Goal value must be greater than 0") };
    }
    return {};
  };

  const validateGoal = () => {
    return {
      ...validateAssignee(),
      ...validatePipeline(),
      ...validateDuration(),
      ...validateTrackingMetric(),
    };
  };

  const errorMessage = (dataKey) => {
    const message = errors[dataKey] || "";
    if (message != "") {
      return <div className={"text-red-600"}>{message}</div>;
    }
  };

  const handleDelete = () => {
    dispatch(GoalAction.deleteGoal(goalId));
    onClose();
  };

  const renderConfirmDialog = () => {
    return (
      <ConfirmDialog
        visible={showConfirmDelete}
        onClose={() => {
          setShowConfirmDelete(false);
        }}
        title={t("Are you sure to delete this goal?")}
        description={t(
          "This will permanently delete and remove all the reports related to this goal."
        )}
        onSubmit={handleDelete}
      />
    );
  };

  return (
    <div className={"h-full w-full p-4"}>
      {renderConfirmDialog()}
      <div className="border-b border-gray-200 bg-white py-5">
        <div className="-mt-4 flex flex-wrap items-center justify-between sm:flex-nowrap">
          <div className="mt-4">
            <div className="flex items-center w-full space-x-2">
              <Label>Goal Type</Label>
              <DataPicker
                onChange={(value) => {
                  handleOnChange({
                    fieldId: ["type"],
                    fieldValue: value.value,
                  });
                }}
                items={goalTypes.toJS()}
                values={selectedGoalType.toJS()}
                isMulti={false}
              />
            </div>
          </div>
          <div className="mt-4 ml-4 flex shrink-0">
            {!isNewGoal() && (
              <button
                type="button"
                onClick={() => {
                  setShowConfirmDelete(true);
                }}
                className="relative inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset hover:bg-gray-50"
              >
                <span>Delete</span>
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="relative ml-3 inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset hover:bg-gray-50"
            >
              <span>Close</span>
            </button>
            <button
              type="button"
              onClick={handleOnSave}
              className="relative ml-3 inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-xs ring-1 ring-blue-300 ring-inset hover:bg-blue-500"
            >
              <span>Save</span>
            </button>
          </div>
        </div>
      </div>

      <div className={"mt-4"}>
        <Label>Assignee</Label>
        <div className={"flex space-x-2"}>
          <div className={"w-36"}>
            <DataPicker
              onChange={(value) => {
                handleOnChange({
                  fieldId: ["scope", "type"],
                  fieldValue: value.value,
                });
              }}
              items={scopeTypes.toJS()}
              values={selectedScopeType.toJS()}
              isMulti={false}
            />
          </div>
          <div className={"w-full"}>
            {renderAssigneePicker()}
            {errorMessage("assignee")}
          </div>
        </div>
      </div>
      <div>
        <Label>Sale funnels</Label>
        <DataPicker
          onChange={(items) => {
            handleOnChange({ fieldId: ["buckets"], fieldValue: items });
          }}
          items={pipelinesOptions}
          values={buckets}
        />
        {errorMessage("buckets")}
      </div>
      <div>
        <Label>Frequency</Label>
        <DataPicker
          onChange={(value) => {
            handleOnChange({ fieldId: ["frequency"], fieldValue: value.value });
          }}
          items={frequencies.toJS()}
          values={selectedFrequency.toJS()}
          isMulti={false}
        />
      </div>
      <div>
        <Label>Duration</Label>
        <div className={"flex space-x-2 w-full"}>
          <div className={"w-full"}>
            <DatePicker
              selected={goal.get("trackingFrom")}
              placeholder={"From"}
              onChange={(value) => {
                handleOnChange({
                  fieldId: ["trackingFrom"],
                  fieldValue: value,
                });
              }}
            />
            {errorMessage("trackingFrom")}
          </div>
          <div>-</div>
          <div className={"w-full"}>
            <DatePicker
              placeholder={"To"}
              selected={goal.get("trackingTo")}
              onChange={(value) => {
                handleOnChange({ fieldId: ["trackingTo"], fieldValue: value });
              }}
            />
            {errorMessage("trackingTo")}
          </div>
        </div>
      </div>
      <div>
        <Label>Tracking metric</Label>
        <div className={"flex space-x-2 align-middle"}>
          <div className={"w-36"}>
            <DataPicker
              onChange={(value) => {
                handleOnChange({
                  fieldId: ["trackingMetric"],
                  fieldValue: value.value,
                });
              }}
              items={trackingMetrics.toJS()}
              values={selectedTrackingMetric.toJS()}
              isMulti={false}
            />
          </div>
          <div>-</div>
          <InputInline
            border={true}
            value={goal.getIn(["trackingGoal", "value"])}
            placeholder={"Insert value"}
            onChange={(newVal) => {
              if (parseFloat(newVal) > 0) {
                handleOnChange({
                  fieldId: ["trackingGoal", "value"],
                  fieldValue: parseFloat(newVal),
                });
              }
            }}
          />
        </div>
        {errorMessage("trackingMetric")}
      </div>
    </div>
  );
}
